import type { IFileLink } from "../../../file";
import type { ExplicitType } from "../../../utils";
import { isOfType } from "../../../utils";
import type { ILinkByUid } from "../utils";

import type { IAttributeValue } from "./values";

export * from "./bounds";
export * from "./values";
export * from "./templates";

// using this instead of an enum will allow us to quickly convert API calls
export type AttributeOrigins =
  | "measured"
  | "predicted"
  | "specified"
  | "computed"
  | "unknown";

// Attributes of a data/spec object have various data
// they are properties, conditions, and parameters
interface IAttributeBase {
  // value for attributes can be one of the below possible options
  value: IAttributeValue;
  name: string;
  notes?: string;
  origin: AttributeOrigins;
  template?: ILinkByUid;
  file_links: IFileLink[];
}

export interface IAttributeCondition extends IAttributeBase {
  type: "condition";
}
export interface IAttributeParameter extends IAttributeBase {
  type: "parameter";
}
export interface IAttributeProperty extends IAttributeBase {
  type: "property";
}
export interface IAttributePropertyAndConditions {
  type: "property_and_conditions";
  conditions: IAttributeCondition[];
  property: IAttributeProperty;
}

export type IScalarAttribute =
  | IAttributeProperty
  | IAttributeCondition
  | IAttributeParameter;
export type ScalarAttributeType = IScalarAttribute["type"];

export type IAttribute = IScalarAttribute | IAttributePropertyAndConditions;
export type AttributeType = IAttribute["type"];

export const isAttributeCondition = isOfType<IAttributeCondition>("condition");
export const isAttributeParameter = isOfType<IAttributeParameter>("parameter");
export const isAttributeProperty = isOfType<IAttributeProperty>("property");
export const isAttributePropertyAndConditions =
  isOfType<IAttributePropertyAndConditions>("property_and_conditions");

export const isScalarAttribute = (
  obj?: ExplicitType<string>
): obj is IScalarAttribute =>
  isAttributeCondition(obj) ||
  isAttributeParameter(obj) ||
  isAttributeProperty(obj);

export const isAttribute = (obj?: ExplicitType<string>): obj is IAttribute =>
  isScalarAttribute(obj) || isAttributePropertyAndConditions(obj);
